import React, { useEffect, useState } from 'react'

import { Button } from 'components/ui/button'
import { Progress } from 'components/ui/progress'

import { CompletionStatus } from '../../hooks/useRunPlaybookReview'

interface LoadingScreenProps {
  cancel: () => void
  completionStatus: CompletionStatus | null
  numRules: number
}

const DEFAULT_STATUS_MESSAGE = 'Running your playbook against this document'

export const LoadingScreen = ({
  cancel,
  completionStatus,
  numRules,
}: LoadingScreenProps) => {
  const [displayPercentage, setDisplayPercentage] = useState(0)
  const targetPercentage = completionStatus?.completion?.percentage || 25

  useEffect(() => {
    if (displayPercentage !== targetPercentage) {
      const animationId = setInterval(() => {
        setDisplayPercentage((current) => {
          const step = (targetPercentage - current) * 0.01
          if (Math.abs(step) < 0.01) {
            return targetPercentage
          }

          return current + step
        })
      }, 50)

      return () => clearInterval(animationId)
    }
  }, [displayPercentage, targetPercentage])

  const statusMessage = (() => {
    const message = completionStatus?.completion?.statusMessage
    if (!message) {
      return DEFAULT_STATUS_MESSAGE
    }

    // We map references to their actual Word ranges after the response
    // returns & before redirecting. Remap the "completed" message so it's transparent
    // to users that things are still happening.
    if (message === 'Completed') {
      return 'Completing'
    }

    return message
  })()

  return (
    <div className="flex h-full flex-col items-center justify-center bg-primary">
      <div className="flex w-[250px] flex-col justify-center">
        <div className="mb-4 flex flex-col gap-1">
          <p className="text-sm font-medium">{statusMessage}…</p>
          <p className="text-xs text-muted">
            {completionStatus?.rulesCompleted ?? 0} of {numRules} rules
          </p>
        </div>
        <Progress
          value={displayPercentage}
          className="mb-8 h-2 w-full max-w-md rounded-sm"
        />
        <Button
          variant="outline"
          className="ml-auto"
          size="sm"
          onClick={cancel}
        >
          Cancel
        </Button>
      </div>
    </div>
  )
}
